import React, { FC, useEffect, useState } from 'react';
import * as propz from 'propz';
import { Tournament } from 'models/tournament';
import { School } from 'models/school';
import { Record as RecordModel } from 'models/record';
import { useTournamentTVData } from 'hooks/useTournamentTVData';
import { getSchoolById } from 'services/school';
import { getNationalAthleticScreensData } from 'utils/tournamentTVUtils';
import { getRecords } from 'services/getRecords';
import {
  isIndividualTournament,
  isRegularIndividualTournamentSubtype,
  isAthleticSwimmingTournamentSubtype,
} from 'helpers/tournament';
import GroupScreen from 'components/TV/TVGroupScreen';
import TVFooter from 'components/TV/TVFooter';
import PageNotFound from 'views/School/PageNotFound';
import './TournamentTV.scss';

interface TournamentTVProps {
  tournament: Tournament;
}

const TournamentTV: FC<TournamentTVProps> = ({ tournament }) => {
  const bigScreenStatus = propz.get(
    tournament,
    ['publicBigscreenSite', 'status'],
    'DISABLED'
  );

  const { events, isSync: isBaseDataSync } = useTournamentTVData(tournament);

  const [unionSchool, setUnionSchool] = useState<School | null>(null);
  const [isSchoolLoading, setIsSchoolLoading] = useState(true);
  useEffect(() => {
    const schoolId = propz.get(tournament, ['schoolId'], '') as string;
    if (!schoolId) {
      setUnionSchool(null);
      setIsSchoolLoading(false);
      return;
    }
    setIsSchoolLoading(true);
    getSchoolById(schoolId)
      .then((school: School) => {
        setUnionSchool(school);
        setIsSchoolLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load union school:', err);
        setUnionSchool(null);
        setIsSchoolLoading(false);
      });
  }, [tournament]);

  const [allRecords, setAllRecords] = useState<RecordModel[]>([]);
  const [isRecordsLoading, setIsRecordsLoading] = useState(true);
  useEffect(() => {
    const schoolId = propz.get(tournament, ['schoolId'], '') as string;
    if (!schoolId) {
      setAllRecords([]);
      setIsRecordsLoading(false);
      return;
    }
    setIsRecordsLoading(true);
    getRecords(schoolId)
      .then((records: RecordModel[]) => {
        console.log('loaded allRecords:', records);
        setAllRecords(records);
        setIsRecordsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load records:', err);
        setAllRecords([]);
        setIsRecordsLoading(false);
      });
  }, [tournament]);

  const [screenData, setScreenData] = useState<any[]>([]);
  const [isScreensReady, setIsScreensReady] = useState(false);
  useEffect(() => {
    if (!isBaseDataSync) return;

    if (isIndividualTournament(tournament)) {
      if (
        isRegularIndividualTournamentSubtype(tournament) ||
        isAthleticSwimmingTournamentSubtype(tournament)
      ) {
        setScreenData(getNationalAthleticScreensData(events));
      } else {
        setScreenData([]);
      }
    } else {
      setScreenData([]);
    }
    setIsScreensReady(true);
  }, [isBaseDataSync, events, tournament]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const autoScrollingDelay =
  (propz.get(tournament, ['publicBigscreenSite', 'autoScrollingDelay'], 6) as number) || 6;

  useEffect(() => {
    if (!isScreensReady || screenData.length === 0) return;

    const slide = screenData[currentIndex];

    const slideDelayMs = autoScrollingDelay * 1000;

    const timeoutId = window.setTimeout(() => {
      setCurrentIndex((idx) =>
        idx < screenData.length - 1 ? idx + 1 : 0
      );
    }, slideDelayMs);

    return () => clearTimeout(timeoutId);
  }, [
    isScreensReady,
    screenData,
    autoScrollingDelay,   
    currentIndex,
  ]);



  if (bigScreenStatus === 'DISABLED') {
    return <PageNotFound />;
  }

  if (isSchoolLoading || !isBaseDataSync || !isScreensReady || isRecordsLoading) {
    return (
      <div style={{ color: 'white', textAlign: 'center', marginTop: '20vh' }}>
        Loading…
      </div>
    );
  }

  const slide = screenData[currentIndex];
  const picUrl = propz.get(
    tournament,
    ['photos', 0, 'picUrl'],
    '/images/default_bigscreen_background.jpg'
  );
  const wrapperStyle = { backgroundImage: `url(${picUrl})` };

  const showFooter = false;

  return (
    <div className="bBigScreenAmbience" style={wrapperStyle}>
      {slide && unionSchool && (
        <GroupScreen
          data={slide}
          tournament={tournament}
          unionSchool={unionSchool}
          allRecords={allRecords}
        />
      )}

      {showFooter && slide && (
        <TVFooter
          events={slide.events}
          activeSchoolId={tournament.schoolId as string}
        />
      )}
    </div>
  );
};

export default TournamentTV;
